import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Ruler, Target, LogOut, Camera, Lock, Eye, EyeOff, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Perfil = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State for modals
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    senha_atual: '',
    nova_senha: '',
    confirmar_senha: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    atual: false,
    nova: false,
    confirmar: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logout realizado com sucesso');
    navigate('/login');
  };

  // Handle photo upload
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }

    setUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        // Update profile photo
        const response = await axios.put(`${API_URL}/api/auth/profile-photo`, {
          foto_perfil: base64
        });
        
        // Update local user state
        if (updateUser && response.data.user) {
          updateUser(response.data.user);
        }
        
        toast.success('Foto atualizada com sucesso!');
        setShowPhotoModal(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erro ao atualizar foto:', error);
      toast.error('Erro ao atualizar foto');
    } finally {
      setUploading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.nova_senha !== passwordData.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (passwordData.nova_senha.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        senha_atual: passwordData.senha_atual,
        nova_senha: passwordData.nova_senha
      });
      
      toast.success('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setPasswordData({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl mx-auto">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-highlight">
            Meu Perfil
          </h2>
          <p className="text-sm text-muted-foreground">
            Suas informações pessoais
          </p>
        </div>

        {/* Profile Header with Photo */}
        <div className="floating-card p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
            {/* Profile Photo */}
            <div className="relative group">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden bg-gradient-to-br from-[#0F2B4C]/20 to-[#B8977A]/20 flex items-center justify-center border-4 border-white shadow-lg">
                {user?.foto_perfil ? (
                  <img 
                    src={user.foto_perfil} 
                    alt={user.nome}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="text-[#B8977A]" size={48} />
                )}
              </div>
              <button
                onClick={() => setShowPhotoModal(true)}
                className="absolute bottom-0 right-0 w-9 h-9 bg-[#0F2B4C] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1C3D66] transition-colors"
              >
                <Camera className="text-white" size={18} />
              </button>
            </div>
            
            <div className="text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold mb-1 text-highlight">
                {user?.nome}
              </h3>
              <p className="text-muted-foreground capitalize text-sm">
                {user?.role === 'patient' ? 'Paciente' : user?.role === 'admin' ? 'Administrador' : 'Médico'}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
              <div className="w-10 h-10 rounded-xl bg-[#0F2B4C]/10 flex items-center justify-center">
                <Mail className="text-[#0F2B4C]" size={18} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                <p className="font-medium text-sm">
                  {user?.email}
                </p>
              </div>
            </div>

            {user?.telefone && (
              <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl">
                <div className="w-10 h-10 rounded-xl bg-[#B8977A]/10 flex items-center justify-center">
                  <Phone className="text-[#B8977A]" size={18} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Telefone</p>
                  <p className="font-medium text-sm">
                    {user.telefone}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Health Info - Only for patients */}
        {user?.role === 'patient' && (
          <div className="floating-card p-6">
            <h3 className="text-lg font-bold mb-4 text-highlight">
              Dados de Saúde
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {user?.altura && (
                <div className="p-4 bg-muted/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Ruler className="text-[#0F2B4C]" size={16} />
                    <p className="text-xs text-muted-foreground">Altura</p>
                  </div>
                  <p className="text-xl font-bold text-highlight">
                    {user.altura} m
                  </p>
                </div>
              )}

              {user?.peso_inicial && (
                <div className="p-4 bg-muted/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="text-[#B8977A]" size={16} />
                    <p className="text-xs text-muted-foreground">Peso Inicial</p>
                  </div>
                  <p className="text-xl font-bold text-highlight">
                    {user.peso_inicial} kg
                  </p>
                </div>
              )}

              {user?.peso_meta && (
                <div className="p-4 bg-gradient-to-br from-[#0F2B4C]/10 to-[#B8977A]/10 rounded-xl col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="text-[#0F2B4C]" size={16} />
                    <p className="text-xs text-[#0F2B4C] font-medium">Meta de Peso</p>
                  </div>
                  <p className="text-2xl font-bold text-[#0F2B4C]">
                    {user.peso_meta} kg
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Security Settings */}
        <div className="floating-card p-6">
          <h3 className="text-lg font-bold mb-4 text-highlight">
            Segurança
          </h3>
          <div className="space-y-3">
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full flex items-center gap-4 p-4 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-[#B8977A]/10 flex items-center justify-center">
                <Lock className="text-[#B8977A]" size={18} />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Alterar Senha</p>
                <p className="text-xs text-muted-foreground">Atualize sua senha de acesso</p>
              </div>
            </button>
          </div>
        </div>

        {/* Logout */}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-3 p-4 bg-red-50 text-red-600 rounded-2xl font-semibold hover:bg-red-100 transition-colors"
        >
          <LogOut size={20} />
          <span>Sair da Conta</span>
        </button>
      </div>

      {/* Photo Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-highlight">Alterar Foto</h3>
              <button
                onClick={() => setShowPhotoModal(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>

            <div className="flex flex-col items-center">
              {/* Current Photo Preview */}
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-[#0F2B4C]/20 to-[#B8977A]/20 flex items-center justify-center mb-6 border-4 border-gray-100">
                {user?.foto_perfil ? (
                  <img 
                    src={user.foto_perfil} 
                    alt={user.nome}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="text-[#B8977A]" size={48} />
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full py-3 bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                {uploading ? 'Enviando...' : 'Escolher Nova Foto'}
              </button>
              
              <p className="text-xs text-muted-foreground mt-3 text-center">
                Formatos aceitos: JPG, PNG, GIF (máx. 5MB)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-highlight">Alterar Senha</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha Atual
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.atual ? 'text' : 'password'}
                    value={passwordData.senha_atual}
                    onChange={(e) => setPasswordData({...passwordData, senha_atual: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, atual: !showPasswords.atual})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.atual ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.nova ? 'text' : 'password'}
                    value={passwordData.nova_senha}
                    onChange={(e) => setPasswordData({...passwordData, nova_senha: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 focus:border-[#0F2B4C]"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, nova: !showPasswords.nova})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.nova ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirmar ? 'text' : 'password'}
                    value={passwordData.confirmar_senha}
                    onChange={(e) => setPasswordData({...passwordData, confirmar_senha: e.target.value})}
                    className={`w-full px-4 py-3 pr-12 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/50 ${
                      passwordData.confirmar_senha && passwordData.nova_senha !== passwordData.confirmar_senha
                        ? 'border-red-300 focus:border-red-500'
                        : passwordData.confirmar_senha && passwordData.nova_senha === passwordData.confirmar_senha
                        ? 'border-green-300 focus:border-green-500'
                        : 'border-gray-200 focus:border-[#0F2B4C]'
                    }`}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, confirmar: !showPasswords.confirmar})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirmar ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {passwordData.confirmar_senha && passwordData.nova_senha !== passwordData.confirmar_senha && (
                  <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
                )}
                {passwordData.confirmar_senha && passwordData.nova_senha === passwordData.confirmar_senha && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <Check size={12} /> Senhas coincidem
                  </p>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
                  }}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={changingPassword || passwordData.nova_senha !== passwordData.confirmar_senha}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-[#0F2B4C] to-[#B8977A] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {changingPassword ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Perfil;
